import { Attribute } from '../../decorators/attribute.decorator';
import { Component } from '../../decorators/component.decorator';
import { element } from '../../global/ui-helpers';
import { UIElement } from '../ui-element';
import styles from './ui-calculator-result.css';
import { Digest } from '../../global/digest';
import { Child } from '../../decorators/child.decorator';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICalculatorResult
 * @element ui-calculator-result
 * @classdesc Represents a class for <code>ui-calculator</code> element.
 * @property {string} label {@attr label} Heading text.
 * @property {string} value {@attr value} Value text.
 * @property {string} id {@attr id} Unique identifier for the result element.
 * @slot
 * @example
 * <ui-calculator-result label="Total value after 10 years" value="1 338€">
 * </ui-calculator-result>
 */
@Component({
    tag: 'ui-calculator-result',
    styles,
})
export class UICalculatorResult extends UIElement {
    @Attribute() label!: string;
    @Attribute() value!: string;
    @Attribute() units?: string;
    @Attribute() id = Digest.randomId();

    @Child(':scope .result-label') _label!: HTMLElement;
    @Child(':scope .result-value') _value!: HTMLElement;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['label', 'value'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        if (this.state !== 'hydrated') {
            return;
        }
        switch (name) {
            case 'label':
                this.setAttribute('label', newValue);
                this._label.textContent = newValue;
                break;
            case 'value':
                this.setAttribute('value', newValue);
                this._value.textContent = newValue;
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        this.insertElements([
            element`<label for="${this.id}" class="result-label">${this.label}</label>`,
            this.createElement({
                tagName: 'div',
                classList: {
                    'result-container': true,
                },
                children: [
                    element`<output id="${this.id}" class="result-value" aria-live="polite">${this.value}</output>`,
                    this.units
                        ? element`<span class="result-units" aria-live="polite">${this.units}</span>`
                        : null,
                ],
            }),
        ]);
    }
}
